import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import jwtDecode, { JwtPayload } from "jwt-decode";
import _, { isArray, isEmpty, isUndefined } from "lodash";
import { IApiResponse } from "model/Common/IApiResponse";
import { IErrorResponse } from "model/Common/IErrorResponse";
import { IRefreshTokenRequest } from "model/Identity/IRefreshTokenRequest";
import moment from "moment";
import { env } from "process";
import qs from "qs";
import configureStore from "redux/configurations/configureStore";
import { IAuthenticate } from "./../model/Identity/IAuthenticate";
import { IdentityActions } from "redux/actions";
import { AlertHelper } from "helpers";
import { ELanguage, I18nTranslator, translations } from "utils/i18n";
import { EAlertType } from "helpers/alertHelper";

const BASE_API = process.env.REACT_APP_BASE_API;

const API_CONFIG: AxiosRequestConfig = {
  // returnRejectedPromiseOnError: true,
  // withCredentials: true,
  timeout: 0,
  baseURL: BASE_API,
  headers: {
    "Content-type": "application/json",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Expose-Headers": "Authorization",
    "ngrok-skip-browser-warning": "true",
  },
  paramsSerializer: (params: any) => qs.stringify(params, { indices: false }),
};

export abstract class BaseApiService {
  private instance: AxiosInstance;
  private needVersion = false;
  constructor(needVersion = true) {
    this.instance = this.initHttp(needVersion);
    this.needVersion = needVersion;
  }

  private initHttp = (needVersion: boolean) => {
    const http = axios.create(API_CONFIG);
    if (needVersion) {
      http.interceptors.request.use(this._injectVersion);
    }
    http.interceptors.request.use(this._injectToken);
    http.interceptors.request.use(this._injectWarehouseId);
    http.interceptors.request.use(this.injectLanguage);
    http.interceptors.request.use((res) => this._injectRefreshToken(res));
    return http;
  };

  private _injectVersion = (config: AxiosRequestConfig): AxiosRequestConfig => {
    try {
      const version = 1;
      if (!config.baseURL?.includes(`/v${version}`)) {
        config.baseURL = config.baseURL + `/v${version}`;
      }
      return config;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  private _injectToken = (config: AxiosRequestConfig): AxiosRequestConfig => {
    try {
      const store = configureStore().store;
      const token = store.getState().IdentityReducer.authenticate?.jwToken;
      if (!_.isEmpty(token) || this.needVersion) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      return config;
    }
  };
  private injectLanguage = (config: AxiosRequestConfig): AxiosRequestConfig => {
    try {
      const store = configureStore().store;
      const language = store.getState().LanguageReducer.language;
      config.headers["accept-language"] = language === ELanguage.VI ? "vi-Vn" : "en-Us";
      return config;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  private _injectWarehouseId = (config: AxiosRequestConfig): AxiosRequestConfig => {
    try {
      const warehouseId = configureStore().store.getState().IdentityReducer.currentWarehouse?.id;
      if (warehouseId) {
        if (config.method === "GET" || config.method === "get") {
          if (!_.isEmpty(config.params)) {
            if (_.isUndefined(config.params["warehouseId"]) || _.isEmpty(config.params["warehouseId"])) {
              config.params["warehouseId"] = warehouseId;
            }
          } else {
            config.params = {
              warehouseId,
            };
          }
        }
        if (config.method === "POST" || config.method === "post" || config.method === "put" || config.method === "PUT") {
          if (!_.isEmpty(config.data) && _.isEmpty(config.data["warehouseId"])) {
            config.data["warehouseId"] = warehouseId;
          }
        }
      }
      return config;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  private _handleNewToken = async (data?: IAuthenticate, status?: number) => {
    const store = configureStore().store;
    if (!_.isEmpty(data)) {
      store.dispatch(IdentityActions.authenticate.success(data!));
      this.onRefreshed(data?.jwToken!);
    } else {
      store.dispatch(IdentityActions.logout.request());
      if (status === 401) {
        AlertHelper.showAlert(I18nTranslator(translations.warningMessages.sessionExpired), EAlertType.ERROR);
      }
    }
  };

  private _injectRefreshToken = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    let orgConfig = Object.assign({}, config);
    const store = configureStore().store;

    if (!this._checkNeedRefreshToken() || config.url?.includes("/identity/refresh-token")) {
      return config;
    }
    const retryReq = new Promise<AxiosRequestConfig>((resolve) => {
      this.subscribeTokenRefresh(async (accessToken) => {
        orgConfig.headers["Authorization"] = "Bearer " + accessToken;
        resolve(orgConfig);
      });
    });

    if (!global.isAppRefreshingToken) {
      if (global.refreshSubscribers === undefined) {
        global.refreshSubscribers = [];
      }
      global.isAppRefreshingToken = true;
      const refreshResponse = await this._refreshToken();
      global.isAppRefreshingToken = false;
      if (_.isEmpty(refreshResponse)) {
        store.dispatch(IdentityActions.logout.request());
      } else {
        this._handleNewToken(refreshResponse?.data.data, refreshResponse?.status);
      }
    }
    return retryReq;
  };

  private _refreshToken = async (): Promise<AxiosResponse<IApiResponse<IAuthenticate>> | null> => {
    try {
      // console.log("[RefreshToken calling]");
      const store = configureStore().store;
      const state = store.getState();
      const identity = state.IdentityReducer;
      var config: AxiosRequestConfig = API_CONFIG;
      config.headers.Authorization = null;
      config.headers.isRefresh = true;

      const request: IRefreshTokenRequest = {
        warehouseId: process.env.REACT_APP_WAREHOUSE_ID!,
        userNameOrEmail: identity.authenticate!.userName!,
        refreshToken: identity.authenticate!.refreshToken!,
        deviceId: identity.deviceId!,
      };
      const response = await axios.create(config).post<IApiResponse<IAuthenticate>>(`${BASE_API}/identity/refresh-token`, request, config);
      // console.log("[RefreshToken successfully]");
      return response;
    } catch (error) {
      // console.log("[RefreshToken failed]", error);
      return null;
    }
  };
  private _checkNeedRefreshToken = (): boolean => {
    const store = configureStore().store;
    const token = store.getState().IdentityReducer.authenticate?.jwToken;
    if (isEmpty(token)) {
      return false;
    }
    const tokenDecode = jwtDecode<JwtPayload>(token!);
    if (moment.unix(tokenDecode.exp!).isBefore(moment())) {
      return true;
    }
    return false;
  };

  subscribeTokenRefresh(cb: (token: string) => void) {
    if (global.refreshSubscribers === undefined) {
      global.refreshSubscribers = [];
    }
    global.refreshSubscribers.push(cb);
  }
  onRefreshed(token: string) {
    global.refreshSubscribers.map((cb) => cb(token));
  }

  convertFormData(data: any) {
    var formData = new FormData();
    for (const key in data) {
      formData.append(key, data[key]);
    }
    return formData;
  }

  private _handleResponse<T = void>(response: AxiosResponse<IApiResponse<T>>): IApiResponse<T> | IErrorResponse {
    if (response.config.url?.includes("Export") && response.config.params["disableDownload"] !== true) {
      const fileName = response.config.params["fileName"];
      const isCsv = response.config.params["isCsv"];
      const isPdf = response.config.params["isPdf"];
      const fileFormat = response.config.params["fileFormat"];

      const url = window.URL.createObjectURL(new Blob([response.data as unknown as string]));
      const link = document.createElement("a");
      link.href = url;
      const getFileName = isCsv ? `${fileName}.csv` : isPdf ? `${fileName}.pdf` : fileFormat ? `${fileName}.${fileFormat}` : `${fileName}.xlsx`;
      link.setAttribute("download", getFileName);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 1000);
      return {
        succeeded: true,
        data: (response?.data as any) || undefined,
        failed: false,
        message: [],
      };
    }
    if (response.data?.succeeded || response.status === 200) {
      return response?.data! || { data: [] };
    }

    const error: IErrorResponse = {
      message: response.data?.message,
      errors: !_.isEmpty(response.data?.errors)
        ? response.data.errors
        : {
            message: {
              Text: !isUndefined(response.data?.message) ? response.data.message[0].Text : "Unknown error",
              Code: !isUndefined(response.data?.message) ? response.data.message[0]?.Code : 69,
            },
          },
    };
    return error;
  }

  private _handleError(err: any): IErrorResponse {
    try {
      const axiosError = err as AxiosError;
      const response = axiosError.response! as AxiosResponse<any>;

      const responseError: IErrorResponse = {
        message: !_.isEmpty(response.data) && response.data?.message && isArray(response.data.message) ? response.data.message : [response.data.message],
        errors: !_.isEmpty(response.data.errors)
          ? response.data.errors
          : {
              message: {
                Text: response.data.message ? response.data.message.Text || response.data.message[0].Text : "System Errors",
                Code: response.data.message ? response.data.message.Code || response.data.message[0].Code : 400,
              },
            },
      };
      return responseError;
    } catch (error: any) {
      const responseError: IErrorResponse = {
        message: [
          {
            Text: error,
            Code: "100",
            Type: 100,
          },
        ],
        errors: {
          message: {
            Text: error,
            Code: "100",
            Type: 100,
          },
        },
      };
      return responseError;
    }
  }

  private async _call<T>(callApi: Promise<AxiosResponse<IApiResponse<T>>>) {
    try {
      const response = await callApi;
      return this._handleResponse<T>(response);
    } catch (error) {
      return this._handleError(error);
    }
  }

  public async get<T>(url: string, config?: AxiosRequestConfig) {
    return this._call<T>(this.instance.get(url, config));
  }

  public async post<P = void, T = void>(url: string, data?: P, config?: AxiosRequestConfig) {
    return this._call<T>(this.instance.post(url, data, config));
  }

  public async put<P = void, T = void>(url: string, data?: P, config?: AxiosRequestConfig) {
    return this._call<T>(this.instance.put(url, data, config));
  }

  public async delete<T = void>(url: string, config?: AxiosRequestConfig) {
    return this._call<T>(this.instance.delete(url, config));
  }

  public async postForm<P = void, T = void>(url: string, data?: P, config?: AxiosRequestConfig) {
    const requestUrl = `${url}?warehouseId=${configureStore().store.getState().IdentityReducer.currentWarehouse?.id}`;
    const formConfig: AxiosRequestConfig = {
      ...config,
      withCredentials: true,
      headers: {
        "Content-Type": "multipart/form-data",
        "Access-Control-Allow-Origin": "*",
      },
      params: data,
    };

    const formData = this.convertFormData(data);
    formData.append("warehouseId", configureStore().store.getState().IdentityReducer.currentWarehouse?.id as string);
    return this._call<T>(this.instance.post(requestUrl, formData, formConfig));
  }
  public download = (url: string, config?: AxiosRequestConfig) => {
    const reConfig: AxiosRequestConfig = {
      ...config,
      withCredentials: true,
      headers: {
        "Access-Control-Allow-Origin": "*",
      },
      responseType: "blob",
    };
    return this.get<any>(url, {
      ...reConfig,
      params: {
        ...reConfig.params,
        pageSize: isEmpty(config?.params["pageSize"]) ? 99999 : config?.params["pageSize"],
        pageNumber: 1,
      },
    });
  };
}
